import { nameToInitials } from "@circleco/ui-kit";
import { t } from "@/i18n-js/instance";
import { getCommunityLogoUrlAndType } from "@/react/helpers/communityHelpers";
import { useThemeContext } from "@/react/providers";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Typography } from "@circle-react-uikit/Typography";
import CircleLogo from "../../../../../assets/images/circular-logo@2x.png";

interface CommunityImageProps {
  community?: CurrentCommunity;
  isLeftAlignLogo?: boolean;
  withCircleBranding?: boolean;
}

const EMPTY_LOGO_DETAILS = {
  url: undefined,
  type: undefined,
};

export const CommunityImage = ({
  community,
  withCircleBranding = false,
  isLeftAlignLogo = false,
}: CommunityImageProps) => {
  const { isDarkAppearance } = useThemeContext();

  const { url, type } = community
    ? getCommunityLogoUrlAndType(community, isDarkAppearance)
    : EMPTY_LOGO_DETAILS;

  const brandColor = community?.prefs?.brand_color;
  const color =
    (isDarkAppearance ? brandColor?.dark : brandColor?.light) || "#506cf0";

  if (!type || withCircleBranding) {
    return (
      <div className="size-12 md:size-16">
        <img loading="lazy" src={CircleLogo} alt={t("circle")} />
      </div>
    );
  }

  if (type === "icon" && !url) {
    return (
      <div className="flex flex-col items-center">
        <div
          className={classNames(
            "mb-3 flex size-12 items-center justify-center rounded-lg text-center",
            {
              "self-start": isLeftAlignLogo,
            },
          )}
          style={{ backgroundColor: color }}
          aria-hidden
        >
          <Typography.TitleSm weight="semibold" color="text-brand-button">
            {nameToInitials(community?.name)}
          </Typography.TitleSm>
        </div>
        <Typography.LabelMd weight="medium">
          {community?.name}
        </Typography.LabelMd>
      </div>
    );
  }

  if (type === "icon" && url) {
    return (
      <div className="flex flex-col items-center">
        <img
          loading="lazy"
          src={url}
          alt=""
          className={classNames("mb-3 h-8", {
            "self-start": isLeftAlignLogo,
          })}
        />
        <Typography.LabelMd weight="medium">
          {community?.name}
        </Typography.LabelMd>
      </div>
    );
  }

  return (
    <img
      loading="lazy"
      className="max-h-9 max-w-[14.375rem]"
      src={url || undefined}
      alt={community?.name ?? ""}
    />
  );
};
